import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminUsers.css";

function AdminUsers() {
  const [admins, setAdmins] = useState([]);

  // Search + filter
  const [search, setSearch]           = useState("");
  const [roleFilter, setRoleFilter]   = useState("");

  // Modal visibility
  const [showAddModal, setShowAddModal]       = useState(false);
  const [showEditModal, setShowEditModal]     = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin]     = useState(null);

  // Form fields
  const [username, setUsername]         = useState("");
  const [password, setPassword]         = useState("");
  const [role, setRole]                 = useState("admin");
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Edit / delete state
  const [editId, setEditId]             = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Notification
  const [notification, setNotification] = useState(null);

  const navigate = useNavigate();
  const API_URL  = "https://api.kemari-corp.com/admins.php";
  const BASE_URL = "https://api.kemari-corp.com/";

  const admin        = JSON.parse(localStorage.getItem("admin"));
  const isSuperAdmin = admin?.role === "superadmin";

  useEffect(() => {
    if (!admin) navigate("/admin/login");
  }, [admin, navigate]);

  const fetchAdmins = async () => {
    try {
      const res  = await fetch(API_URL);
      const data = await res.json();
      setAdmins(data);
    } catch (err) { 
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  // ─── filtered list ────────────────────────────────────
  const displayedAdmins = admins.filter((item) => {
    const matchesRole   = roleFilter === "" || item.role === roleFilter;
    const matchesSearch =
      item.username.toLowerCase().includes(search.toLowerCase());
    return matchesRole && matchesSearch;
  });

  // ─── notification ─────────────────────────────────────
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ─── image ────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setProfileImage(null);
    setImagePreview(null);
  };

  // ─── helpers ──────────────────────────────────────────────
  const resetForm = () => {
    setUsername("");
    setPassword("");
    setRole("admin");
    setProfileImage(null);
    setImagePreview(null);
    setEditId(null);
  };

  const openAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEdit = (item) => {
    setUsername(item.username);
    setPassword("");
    setEditId(item.id);
    setRole(item.role || "admin");
    setProfileImage(null);
    setImagePreview(item.profile_image ? `${BASE_URL}${item.profile_image}` : null);
    setSelectedAdmin(null);
    setShowEditModal(true);
  };

  const openDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  // ─── submit ───────────────────────────────────────────────
  const handleSubmit = async (isEdit = false) => {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);
    formData.append("role", role);
    formData.append("admin_id", admin.id);
    if (profileImage) formData.append("profile_image", profileImage);
    if (isEdit)       formData.append("id", editId);

    try {
      const res  = await fetch(API_URL, { method: "POST", body: formData });
      const data = await res.json();
      showNotification(data.message, "success");

      // ✅ If the logged-in admin edited their own account,
      //    re-fetch the updated record, sync localStorage,
      //    then fire a custom event so Sidebar re-renders immediately.
      if (isEdit && editId === admin.id) {
        const updatedRes  = await fetch(API_URL);
        const allAdmins   = await updatedRes.json();
        const updatedSelf = allAdmins.find((a) => a.id === admin.id);
        if (updatedSelf) {
          localStorage.setItem("admin", JSON.stringify(updatedSelf));
          window.dispatchEvent(new Event("adminUpdated")); // ✅ triggers Sidebar re-render
        }
      }

      resetForm();
      setShowAddModal(false);
      setShowEditModal(false);
      fetchAdmins();
    } catch {
      showNotification("Request failed.", "error");
    }
  };

  // ─── delete ───────────────────────────────────────────────
  const confirmDelete = async () => {
    try {
      const res  = await fetch(API_URL, {
        method:  "DELETE",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ id: deleteTarget.id, admin_id: admin.id }),
      });
      const data = await res.json();
      showNotification(data.message, "success");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchAdmins();
    } catch {
      showNotification("Delete failed.", "error");
    }
  };

  // ─── image upload block ───────────────────────────────────
  const ImageUpload = ({ isEdit }) => (
    <div className="au-image-upload-wrapper">
      <label htmlFor="au-file-input" className="au-image-upload-label">
        {imagePreview ? (
          <img src={imagePreview} alt="Preview" className="au-image-preview" />
        ) : (
          <div className="au-image-placeholder">
            <span className="au-image-icon">📷</span>
            <p>{isEdit ? "Click to change photo" : "Upload profile photo"}</p>
          </div>
        )}
      </label>
      <input
        id="au-file-input"
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: "none" }}
      />
      {imagePreview && (
        <button type="button" className="au-remove-img-btn" onClick={removeImage}>
          ✕ Remove
        </button>
      )}
    </div>
  );

  // ─── role selector block ──────────────────────────────────
  const RoleSelector = () => (
    <div className="au-field">
      <label className="au-label">User Type</label>
      <div className="au-role-toggle">
        {[
          { val: "admin",      label: "Admin" },
          { val: "superadmin", label: "Super Admin" },
        ].map(({ val, label }) => (
          <button
            key={val}
            type="button"
            className={`au-role-toggle-btn ${role === val ? "active" : ""}`}
            onClick={() => setRole(val)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="au-root">

      {/* ── Page Header ── */}
      <div className="au-page-header">
        <h2 className="au-headline">Admin Users</h2>
        {isSuperAdmin && (
          <button className="au-add-btn" onClick={openAdd}>Add User</button>
        )}
      </div>

      {/* ── Filter / Search Bar ── */}
      <div className="au-filter-bar">
        <div className="au-search-wrap">
          <svg className="au-search-icon" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className="au-search"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="au-pill-group">
          {[
            { val: "",           label: "All" },
            { val: "admin",      label: "Admin" },
            { val: "superadmin", label: "Super Admin" },
          ].map(({ val, label }) => (
            <button
              key={val}
              className={`au-pill ${roleFilter === val ? "active" : ""}`}
              onClick={() => setRoleFilter(val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── User List ── */}
      <div className="au-list">
        {displayedAdmins.length === 0 ? (
          <p className="au-empty">No admin users found.</p>
        ) : (
          displayedAdmins.map((item, i) => (
            <div
              key={item.id}
              className="au-row"
              style={{ animationDelay: `${i * 40}ms` }}
              onClick={() => setSelectedAdmin(item)}
            >
              {/* Avatar */}
              {item.profile_image ? (
                <img
                  src={`${BASE_URL}${item.profile_image}`}
                  alt={item.username}
                  className="au-row-avatar"
                />
              ) : (
                <div className="au-row-avatar-placeholder">
                  {item.username?.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Name */}
              <div className="au-row-info">
                <span className="au-row-name">{item.username}</span>
              </div>

              {/* Role badge + chevron */}
              <div className="au-row-right">
                <span className={`au-role-badge au-role-badge--${item.role}`}>
                  {item.role}
                </span>
                <svg className="au-chevron" viewBox="0 0 8 14" fill="none">
                  <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ══ VIEW / DETAIL MODAL ════════════════════════════════ */}
      {selectedAdmin && (
        <div className="au-overlay" onClick={() => setSelectedAdmin(null)}>
          <div className="au-modal" onClick={(e) => e.stopPropagation()}>

            {/* Fixed top */}
            <div className="au-modal-fixed-top">
              <div className="au-handle" />
              <div className="au-modal-top">
                {selectedAdmin.profile_image ? (
                  <img
                    src={`${BASE_URL}${selectedAdmin.profile_image}`}
                    alt={selectedAdmin.username}
                    className="au-modal-avatar-img"
                  />
                ) : (
                  <div className="au-modal-avatar">
                    {selectedAdmin.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="au-modal-meta">
                  <p className="au-modal-name">{selectedAdmin.username}</p>
                  <p className="au-modal-id">ID #{selectedAdmin.id}</p>
                </div>
                <span className={`au-role-badge au-role-badge--${selectedAdmin.role}`}>
                  {selectedAdmin.role}
                </span>
              </div>
              <div className="au-modal-divider" />
            </div>

            {/* Scrollable middle */}
            <div className="au-modal-scroll">
              <div className="au-detail-section">
                <span className="au-detail-label">Created</span>
                <p className="au-detail-value">{selectedAdmin.created_at || "—"}</p>
              </div>
            </div>

            {/* Fixed bottom */}
            <div className="au-modal-fixed-bottom">
              {(isSuperAdmin || admin?.id === selectedAdmin.id) && (
                <div className="au-modal-actions">
                  <button
                    className="au-action-btn au-action-btn--edit"
                    onClick={() => openEdit(selectedAdmin)}
                  >
                    Edit
                  </button>
                  {isSuperAdmin && admin?.id !== selectedAdmin.id && (
                    <button
                      className="au-action-btn au-action-btn--delete"
                      onClick={() => {
                        setSelectedAdmin(null);
                        openDelete(selectedAdmin);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
              <button className="au-close-btn" onClick={() => setSelectedAdmin(null)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══ ADD MODAL ══════════════════════════════════════════ */}
      {showAddModal && (
        <div className="au-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="au-modal" onClick={(e) => e.stopPropagation()}>

            <div className="au-modal-fixed-top">
              <div className="au-handle" />
              <div className="au-modal-add-header">
                <h3 className="au-modal-add-title">Add Admin User</h3>
              </div>
              <div className="au-modal-divider" />
            </div>

            <div className="au-modal-scroll">
              <div className="au-form-grid">
                <ImageUpload isEdit={false} />
                <div className="au-field">
                  <label className="au-label">Username</label>
                  <input
                    type="text"
                    className="au-input"
                    placeholder="Enter username…"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="au-field">
                  <label className="au-label">Password</label>
                  <input
                    type="password"
                    className="au-input"
                    placeholder="Enter password…"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {isSuperAdmin && <RoleSelector />}
              </div>
            </div>

            <div className="au-modal-fixed-bottom">
              <button className="au-save-btn" onClick={() => handleSubmit(false)}>Add Admin</button>
              <button className="au-close-btn" onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
            </div>

          </div>
        </div>
      )}

      {/* ══ EDIT MODAL ═════════════════════════════════════════ */}
      {showEditModal && (
        <div className="au-overlay" onClick={() => { setShowEditModal(false); resetForm(); }}>
          <div className="au-modal" onClick={(e) => e.stopPropagation()}>

            <div className="au-modal-fixed-top">
              <div className="au-handle" />
              <div className="au-modal-add-header">
                <h3 className="au-modal-add-title">Edit Admin User</h3>
              </div>
              <div className="au-modal-divider" />
            </div>

            <div className="au-modal-scroll">
              <div className="au-form-grid">
                <ImageUpload isEdit={true} />
                <div className="au-field">
                  <label className="au-label">Username</label>
                  <input
                    type="text"
                    className="au-input"
                    placeholder="Enter username…"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="au-field">
                  <label className="au-label">Password</label>
                  <input
                    type="password"
                    className="au-input"
                    placeholder="Leave blank to keep current…"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {isSuperAdmin && <RoleSelector />}
              </div>
            </div>

            <div className="au-modal-fixed-bottom">
              <button className="au-save-btn" onClick={() => handleSubmit(true)}>Update Admin</button>
              <button className="au-close-btn" onClick={() => { setShowEditModal(false); resetForm(); }}>Cancel</button>
            </div>

          </div>
        </div>
      )}

      {/* ══ DELETE MODAL ═══════════════════════════════════════ */}
      {showDeleteModal && (
        <div className="au-overlay au-overlay--delete" onClick={() => setShowDeleteModal(false)}>
          <div className="au-delete-modal" onClick={(e) => e.stopPropagation()}>

            <div className="au-delete-icon-wrap">
              <svg className="au-delete-icon" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>

            <h3 className="au-delete-title">Delete Admin</h3>
            <p className="au-delete-subtitle">
              Are you sure you want to delete
              {deleteTarget && <span className="au-delete-name"> "{deleteTarget.username}"</span>}
              ? This action cannot be undone.
            </p>

            <div className="au-delete-actions">
              <button className="au-delete-confirm-btn" onClick={confirmDelete}>Delete</button>
              <button className="au-close-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>

          </div>
        </div>
      )}

      {/* ══ NOTIFICATION TOAST ════════════════════════════════ */}
      {notification && (
        <div className={`au-notification au-notification--${notification.type}`}>
          <div className="au-notification-content">
            <div className="au-notification-icon">
              {notification.type === "success" ? "✓" : "✕"}
            </div>
            <span className="au-notification-message">{notification.message}</span>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminUsers;